import client from "services/ngage/api-client";
import {Message, MessageData} from "types/Message";
import deserializeMessage from "services/ngage/deserializers/message";
import DataResource from "types/DataResource";
import {parameters} from "../string";

type MessageFilters = {
    scheduled?: boolean
    search?: string | null
    page?: number
    perPage?: number
    sort?: string | null
}

type MessageTestData = {
    messageId: number,
    recipientIds: Array<number | string>
}

/**
 * Get all messages (paginated)
 */
const all = async (page: number = 1) => {
    try {
        const resource = (await client.get<DataResource<Message> | null>(`/messages?page=${page}`)).data || null

        if (resource) {
            resource.data = resource.data.map(deserializeMessage)
        }

        return resource ?? null

    } catch (error) {
        console.error(`Could not retrieve messages`, error)
        return null
    }
}

/**
 * Get all messages that match the given filters (paginated)
 */
const filtered = async (filters: MessageFilters = {}) => {
    const params = parameters(filters, {page: 1, per_page: 25});
    const response = await client.get<DataResource<Message> | null>('/messages', {params})

    if (response.data) {
        response.data.data = response.data.data.map(deserializeMessage)
    }

    return response.data
}

/**
 * Get a single message
 *
 * @param {number} id
 */
const get = async (id: number) => {
    return deserializeMessage(
        (await client.get<Message>(`/messages/${id}`)).data
    )
}

/**
 * Send a single message to a selected set of users
 *
 * @param {object} data
 */
const sendTest = async (data: MessageTestData) => {
    return deserializeMessage(
        (await client.post<Message>(`/messages/${data.messageId}/transmissions`, {
            mode: 'test',
            recipient_ids: data.recipientIds
        })).data
    )
}

/**
 * Send a single message to its current target audience
 *
 * @param id
 */
const send = async (id: number) => {
    return deserializeMessage(
        (await client.post<Message>(`/messages/${id}/transmissions`)).data
    )
}

/**
 * Store a new message
 *
 * @param data
 */
const store = async (data: MessageData) => {
    return deserializeMessage(
        (await client.post<Message>('/messages', data)).data
    )
}

/**
 * Update an existing message
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: MessageData) => {
    return deserializeMessage(
        (await client.put<Message>(`/messages/${id}`, data)).data
    )
}

/**
 * Permanently delete a message
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    try {
        return (await client.delete<null>(`/messages/${id}`)).status === 204
    } catch (error) {
        console.error(`Could not delete message ${id}`, error)
        return false
    }
}

const messages = {
    all,
    filtered,
    get,
    sendTest,
    send,
    store,
    update,
    destroy
}

export default messages;